import React, { useEffect, useState } from "react";
import { api, ROLES, MODULES, formatDateTime } from "@/lib/api";
import { PermissionsPicker } from "@/components/PermissionsPicker";
import { StaffEditDialog } from "@/components/StaffEditDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Users, Shield, KeyRound, ScrollText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ACTION_STYLE = { "staff.create": "bg-emerald-600", "staff.update": "bg-amber-500", "staff.delete": "bg-rose-600" };

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Card className="mt-6" data-testid="audit-log">
      <div className="p-3 border-b font-semibold flex items-center gap-2"><ScrollText className="w-4 h-4" /> Admin Audit Log <span className="text-xs font-normal text-muted-foreground">— who created / changed / deleted accounts</span></div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase font-mono text-muted-foreground sticky top-0"><tr className="text-left"><th className="p-2">When</th><th className="p-2">Action</th><th className="p-2">By</th><th className="p-2">Target</th><th className="p-2">Detail</th></tr></thead>
          <tbody data-testid="audit-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`audit-${r.id}`}>
                <td className="p-2 whitespace-nowrap text-xs">{formatDateTime(r.at)}</td>
                <td className="p-2"><Badge className={ACTION_STYLE[r.action] || "bg-slate-500"}>{r.action.replace("staff.", "")}</Badge></td>
                <td className="p-2">{r.actor_name || "—"}</td>
                <td className="p-2">{r.target_name || "—"}</td>
                <td className="p-2 text-xs text-muted-foreground">{r.detail}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No audit entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const ALL_KEYS = MODULES.map((m) => m.key);
const emptyForm = { name: "", email: "", password: "", role: "customer_service", permissions: ALL_KEYS, leave_quota: 14 };

function AccessDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState(user.permissions ?? ALL_KEYS);
  const save = async () => {
    await api.patch(`/users/${user.id}`, { permissions: perms });
    toast.success("Access updated");
    setOpen(false);
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setPerms(user.permissions ?? ALL_KEYS); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`access-btn-${user.email}`}><KeyRound className="w-3.5 h-3.5 mr-1" /> Access</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Access for {user.name}</DialogTitle></DialogHeader>
        <PermissionsPicker value={perms} onChange={setPerms} testidPrefix="edit-perm" />
        <Button onClick={save} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="access-save">Save Access</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Settings() {
  const [allUsers, setAllUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const { user: me } = useAuth();
  const users = allUsers.filter((u) => u.role !== "agent");

  const load = () => api.get("/users").then((r) => setAllUsers(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      toast.success("Staff added");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  const changeRole = async (id, role) => {
    await api.patch(`/users/${id}`, { role });
    toast.success("Role updated");
    load();
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const isSuper = !!me?.is_super;
  const canManage = isSuper || (me?.permissions ? me.permissions.includes("settings") : true);
  const roleOptions = ROLES.filter((r) => r.value !== "admin" || isSuper);

  return (
    <div className="space-y-4" data-testid="settings-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2"><Users className="w-6 h-6" /> Staff & Roles</h1>
          <p className="text-sm text-muted-foreground">Manage staff accounts and access</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-1" /> Add Staff</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Staff Account</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Name *</Label><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="user-name" /></div>
                <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="user-email" /></div>
                <div><Label>Password *</Label><Input required type="password" minLength="6" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} data-testid="user-password" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{roleOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <PermissionsPicker value={form.permissions} onChange={(p) => setForm({ ...form, permissions: p })} testidPrefix="new-perm" />
                <div><Label>Annual leave quota (days)</Label><Input type="number" min="0" value={form.leave_quota} onChange={e=>setForm({...form,leave_quota:e.target.value})} data-testid="user-leave-quota" /></div>
                <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="user-submit">Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-3">
        {users.map((u) => (
          <Card key={u.id} className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`user-row-${u.email}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 flex items-center justify-center font-bold">
                {u.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold">{u.name} {u.role === "admin" && <Shield className="w-3 h-3 inline text-amber-500" />}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
                {u.role !== "admin" && (
                  <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`perm-summary-${u.email}`}>
                    Access: {(u.permissions ?? ALL_KEYS).length === ALL_KEYS.length ? "All modules" : (u.permissions?.length ? u.permissions.map((k) => MODULES.find((m) => m.key === k)?.label.split(" (")[0] || k).join(", ") : "None")}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canManage && u.role !== "admin" ? (
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Badge variant="secondary">{u.role}{u.is_super ? " • super" : ""}</Badge>
              )}
              {canManage && u.role !== "admin" && <AccessDialog user={u} onSaved={load} />}
              {canManage && u.role !== "admin" && (
                <Button size="sm" variant={u.active ? "outline" : "destructive"} onClick={() => toggleActive(u)}>
                  {u.active ? "Active" : "Disabled"}
                </Button>
              )}
              {(u.role === "admin" ? isSuper : canManage) && <StaffEditDialog user={u} onSaved={load} showQuota={false} superMode={isSuper} />}
            </div>
          </Card>
        ))}
      </div>
      {isSuper && <AuditLog />}
    </div>
  );
}
